import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, GiftIcon, PlusIcon, TagIcon, XIcon } from 'lucide-react';
interface RewardVoucher {
  code: string;
  label: string;
  description: string;
  value: string;
  minimumOrder: number;
  accentClass: string;
}
interface RewardsVouchersSectionProps {
  voucherCode: string;
  orderTotal: number;
  onVoucherCodeChange: (value: string) => void;
  onApplyVoucher: (code: string) => void;
  onViewAllVouchers: () => void;
}
const rewardVouchers: RewardVoucher[] = [{
  code: 'RABEH20',
  label: 'Rabeh reward',
  description: 'Redeem your loyalty points',
  value: 'SAR 20 off',
  minimumOrder: 500,
  accentClass: 'border-[#b9e6c9] bg-[#f2fbf5] text-[#166534]'
}, {
  code: 'FREESHIP',
  label: 'Delivery reward',
  description: 'On eligible orders',
  value: 'Free delivery',
  minimumOrder: 250,
  accentClass: 'border-[#c8d9f4] bg-[#f5f8ff] text-[#1E2B55]'
}, {
  code: 'WELCOME10',
  label: 'Welcome reward',
  description: 'For your next order',
  value: '10% off',
  minimumOrder: 3000,
  accentClass: 'border-[#f0d5a8] bg-[#fff9ed] text-[#92400e]'
}];
export function RewardsVouchersSection({
  voucherCode,
  orderTotal,
  onVoucherCodeChange,
  onApplyVoucher,
  onViewAllVouchers
}: RewardsVouchersSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const pointerRef = useRef({
    id: 0,
    x: 0,
    scrollLeft: 0
  });
  const didDragRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedReward, setSelectedReward] = useState<string | null>(null);
  const [isVoucherSheetOpen, setIsVoucherSheetOpen] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  useEffect(() => {
    if (!isVoucherSheetOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsVoucherSheetOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVoucherSheetOpen]);
  const openVoucherSheet = () => {
    setIsVoucherSheetOpen(true);
    window.setTimeout(() => inputRef.current?.focus(), 250);
  };
  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!scrollRef.current || event.pointerType === 'touch') return;
    pointerRef.current = {
      id: event.pointerId,
      x: event.clientX,
      scrollLeft: scrollRef.current.scrollLeft
    };
    didDragRef.current = false;
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging || event.pointerId !== pointerRef.current.id || !scrollRef.current) return;
    const distance = event.clientX - pointerRef.current.x;
    if (Math.abs(distance) > 4) didDragRef.current = true;
    scrollRef.current.scrollLeft = pointerRef.current.scrollLeft - distance;
  };
  const stopDragging = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerId !== pointerRef.current.id) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsDragging(false);
    window.setTimeout(() => {
      didDragRef.current = false;
    }, 0);
  };
  const applyReward = (reward: RewardVoucher) => {
    if (didDragRef.current || orderTotal < reward.minimumOrder) return;
    const code = reward.code.trim().toUpperCase();
    setSelectedReward(code);
    onVoucherCodeChange(code);
    onApplyVoucher(code);
  };
  const availableRewards = rewardVouchers.filter(reward => orderTotal >= reward.minimumOrder).length;
  return <section aria-labelledby="rewards-vouchers-title" className="overflow-hidden rounded-xl border border-gray-100 bg-white" data-id="element-2081" data-mp="29nug5cwb8458">
      <header className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-4 sm:px-5" data-id="element-2082" data-mp="24qksa1okk0ji">
        <div className="flex min-w-0 items-center gap-3" data-id="element-2083" data-mp="8or5eo2qaw1n">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#f2f4fa] text-[#1E2B55]" data-id="element-2084" data-mp="dkppw0kxrtws">
            <GiftIcon className="h-5 w-5" data-id="element-2085" data-mp="3v1z69t8ghp3t" />
          </span>
          <div className="min-w-0" data-id="element-2086" data-mp="3v670ux8401x">
            <h2 id="rewards-vouchers-title" className="text-base font-bold text-gray-900" data-id="element-2087" data-mp="3u4uem0k75i3">
              Rewards &amp; Vouchers
            </h2>
            <p className="mt-0.5 text-xs text-gray-500" data-id="element-2088" data-mp="3r0e00qqfg92l">
              {availableRewards > 0 ? `${availableRewards} reward${availableRewards > 1 ? 's' : ''} available for this order` : 'Add items to unlock available rewards'}
            </p>
          </div>
        </div>
        <button type="button" onClick={onViewAllVouchers} className="flex-shrink-0 rounded-md px-2 py-1 text-xs font-bold text-[#1E2B55] transition-colors duration-150 hover:bg-[#f2f4fa]" data-id="element-2089" data-mp="1abi7j2nipu0o">
          View all
        </button>
      </header>

      <div className="p-4 sm:p-5" data-id="element-2090" data-mp="2uu2hgl1vtv94">
        <div ref={scrollRef} role="region" aria-label="Available rewards and vouchers" onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={stopDragging} onPointerCancel={stopDragging} className={`-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 pt-1 [scrollbar-width:none] md:mx-0 md:grid md:grid-cols-2 md:overflow-visible md:px-0 md:pb-0 [&::-webkit-scrollbar]:hidden ${isDragging ? 'cursor-grabbing select-none' : 'cursor-grab md:cursor-default'}`} data-id="element-2091" data-mp="2uuu3p59lagmq">
          <button type="button" onClick={openVoucherSheet} className="flex h-[132px] w-[116px] flex-shrink-0 snap-start flex-col justify-between rounded-lg border border-dashed border-gray-300 bg-white p-3 text-left transition-colors duration-150 hover:border-[#1E2B55] md:h-[144px] md:w-auto" data-id="element-2092" data-mp="3ni85bgo8g0d6">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#f2f4fa] text-[#1E2B55]" data-id="element-2093" data-mp="3t8svanq2xa4v">
              <PlusIcon className="h-4 w-4" data-id="element-2094" data-mp="22rgc42ztwdy" />
            </span>
            <span data-id="element-2095" data-mp="617dv1wpan5m">
              <span className="block text-xs font-bold text-gray-900" data-id="element-2096" data-mp="60p91gvz30m3">Add voucher</span>
              <span className="mt-0.5 block text-[11px] leading-4 text-gray-500" data-id="element-2097" data-mp="b24uha22b950">Enter your code</span>
            </span>
          </button>

          {rewardVouchers.map((reward, index) => {
          const isEligible = orderTotal >= reward.minimumOrder;
          const remaining = reward.minimumOrder - orderTotal;
          const isSelected = selectedReward === reward.code || voucherCode === reward.code;
          return <motion.button key={reward.code} type="button" onClick={() => applyReward(reward)} disabled={!isEligible} initial={prefersReducedMotion ? false : {
            opacity: 0,
            x: 12
          }} animate={{
            opacity: isEligible ? 1 : 0.52,
            x: 0,
            y: isSelected && !prefersReducedMotion ? -2 : 0
          }} transition={{
            duration: 0.22,
            delay: prefersReducedMotion ? 0 : index * 0.04,
            ease: [0.22, 1, 0.36, 1]
          }} className={`relative h-[132px] w-[184px] flex-shrink-0 snap-start overflow-hidden rounded-lg border p-3 text-left md:h-[144px] md:w-auto ${isEligible ? `${reward.accentClass} cursor-pointer` : 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'} ${isSelected ? 'ring-2 ring-[#1E2B55]/30 ring-offset-1' : ''}`} aria-label={isEligible ? `Apply ${reward.value}: ${reward.label}` : `${reward.label} unavailable`} data-id="element-2098" data-mp="ttnst3l55z0p" data-mpi={index}>
                <span className={`absolute right-0 top-0 h-7 w-7 border-b border-l ${isEligible ? 'border-current/15 bg-white/45' : 'border-gray-200 bg-white/60'}`} data-id="element-2099" data-mp="2ak83hcwyqfe1" />
                <div className="mb-3 flex items-start justify-between gap-2" data-id="element-2100" data-mp="2u6xo4lssygw4">
                  <span className="text-[11px] font-semibold opacity-70" data-id="element-2101" data-mp="2yzqs37gg5l75">{reward.label}</span>
                  {isEligible ? <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/70" data-id="element-2102" data-mp="2k7yfh381el94">
                      <TagIcon className="h-3 w-3" data-id="element-2103" data-mp="2p6t44lsor415" />
                    </span> : <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-gray-500" data-id="element-2104" data-mp="3cwvr4nhqtjsw">
                      Locked
                    </span>}
                </div>
                <p className="text-sm font-bold" data-id="element-2105" data-mp="37z40rxwqy5qt">{reward.value}</p>
                <p className="mt-0.5 text-[11px] leading-4 opacity-70" data-id="element-2106" data-mp="381gjwee8qm5g">{reward.description}</p>
                <div className="absolute bottom-3 left-3 right-3 border-t border-current/10 pt-2" data-id="element-2107" data-mp="1rnqe937h3ptp">
                  {isEligible ? <span className="flex items-center gap-1 text-[10px] font-bold" data-id="element-2108" data-mp="embzk9nbor0y">
                      <CheckIcon className="h-3 w-3" data-id="element-2109" data-mp="9o0qw28rkmkb" /> Available now
                    </span> : <span className="block text-[10px] font-medium leading-3 text-gray-500" data-id="element-2110" data-mp="nntgtvcguyr8">
                      Add SAR {remaining.toFixed(0)} more to unlock
                    </span>}
                </div>
              </motion.button>;
        })}
        </div>

      </div>

      <AnimatePresence data-id="element-2111" data-mp="3n3q0vme6272o">
        {isVoucherSheetOpen && <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-labelledby="voucher-sheet-title" data-id="element-2112" data-mp="4onw8vbnib76">
            <motion.button type="button" aria-label="Close voucher entry" onClick={() => setIsVoucherSheetOpen(false)} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: prefersReducedMotion ? 0 : 0.18,
          ease: [0.22, 1, 0.36, 1]
        }} className="absolute inset-0 h-full w-full cursor-default bg-slate-950/35" data-id="element-2113" data-mp="3w2tliuszeuja" />
            <div className="absolute inset-x-0 bottom-0 md:bottom-auto md:left-1/2 md:top-1/2 md:w-full md:max-w-md md:-translate-x-1/2 md:-translate-y-1/2" data-id="element-2114" data-mp="1dbl893w27vw2">
              <motion.div initial={prefersReducedMotion ? false : {
            opacity: 0,
            y: 24
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={prefersReducedMotion ? {
            opacity: 0
          } : {
            opacity: 0,
            y: 16
          }} transition={{
            duration: prefersReducedMotion ? 0 : 0.24,
            ease: [0.22, 1, 0.36, 1]
          }} className="rounded-t-xl bg-white shadow-2xl md:rounded-xl" data-id="element-2115" data-mp="18c084q55lhhr">
                <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4" data-id="element-2116" data-mp="pk7gp05qg4c5">
                  <div data-id="element-2117" data-mp="fnulfxujhifa">
                    <h3 id="voucher-sheet-title" className="text-base font-bold text-gray-900" data-id="element-2118" data-mp="fnkvga9hvb9l">Add voucher</h3>
                    <p className="mt-0.5 text-xs text-gray-500" data-id="element-2119" data-mp="2ra4vs48e9fmu">Enter a valid voucher code to apply it to your order.</p>
                  </div>
                  <button type="button" onClick={() => setIsVoucherSheetOpen(false)} aria-label="Close" className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900" data-id="element-2120" data-mp="2wcdqb50x0pr2">
                    <XIcon className="h-5 w-5" data-id="element-2121" data-mp="28lzur4j4g8rq" />
                  </button>
                </div>
                <div className="p-5" data-id="element-2122" data-mp="2dkkxbaluj4jc">
                  <label htmlFor="voucher-code" className="mb-2 block text-sm font-semibold text-gray-800" data-id="element-2123" data-mp="2dhf7ft5e7ymk">Voucher code</label>
                  <input ref={inputRef} id="voucher-code" type="text" value={voucherCode} onChange={event => onVoucherCodeChange(event.target.value.toUpperCase())} placeholder="Enter voucher code" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1E2B55] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1E2B55]/15" data-id="element-2124" data-mp="2mcmvt3ccuaqz" />
                  <button type="button" onClick={() => {
                onApplyVoucher(voucherCode);
                setIsVoucherSheetOpen(false);
              }} disabled={!voucherCode.trim()} className="mt-3 flex w-full items-center justify-center rounded-lg bg-[#1E2B55] px-5 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#162041] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500" data-id="element-2125" data-mp="36yc6z7lf5x0m">
                    Apply voucher
                  </button>
                </div>
              </motion.div>
            </div>
          </div>}
      </AnimatePresence>
    </section>;
}